import { useState, useEffect, createContext, useContext } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, dateOfBirth?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Tag Sentry events with the user id (no PII)
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      } else {
        Sentry.setUser(null);
      }

      // Sync OAuth signups (Google/Apple) to HubSpot
      if (event === "SIGNED_IN" && session?.user) {
        // Record partner referral if a ?ref= code was captured before signup
        import("@/lib/partnerReferral").then(({ recordReferralForUser }) => {
          recordReferralForUser(session.user.id).catch((err) =>
            console.error("Partner referral record failed:", err)
          );
        });

        const provider = session.user.app_metadata?.provider;
        if (provider && provider !== "email") {
          const meta = session.user.user_metadata;
          supabase.functions.invoke("hubspot-sync", {
            body: {
              email: session.user.email,
              name: meta?.full_name || meta?.name || "",
              signup_method: provider, // "google" or "apple"
            },
          }).catch((err) => console.error("HubSpot OAuth sync failed:", err));
        }
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, name: string, dateOfBirth?: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          ...(dateOfBirth ? { date_of_birth: dateOfBirth } : {}),
        },
      },
    });
    if (!error) {
      // Non-blocking HubSpot contact sync
      supabase.functions.invoke("hubspot-sync", {
        body: { email, name, date_of_birth: dateOfBirth, signup_method: "email" },
      }).catch((err) => console.error("HubSpot sync failed:", err));
    }
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ session, user, loading, signUp, signIn, signOut, resetPassword, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
